import React, { useContext, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { Button } from '../components';
import { IdentityContext, IdentityProvider } from '../context';
import { useIndex, useShow } from '../hooks';

export function WillPage() {
  console.log('WillPage');

  return (
    <IdentityProvider>
      <WillProxy />
    </IdentityProvider>
  );
}

export function WillProxy() {
  const { person } = useContext(IdentityContext);
  console.log('WillProxy', person);

  if (!person) {
    return (
      <div>
        <h2>Must fill out profile to continue</h2>
        <Link to="/profile">
          <Button>Create Profile</Button>
        </Link>
      </div>
    );
  }

  return <WillComponent />;
}

function WillDataProvider({ will }) {
  console.log('WillDataProvider', will);

  const { data: assets, loading } = useIndex('assets', { will_id: will.id }, !will);

  if (loading) {
    return <div>Loading will data...</div>;
  }

  return (
    <div>
      <h1>Will: {will.name}</h1>
      <Executors will={will} />
      {assets.map((asset) => (
        <div key={asset.id}>
          <h3>{asset.name}</h3>
          <p>{asset.description}</p>
          <Beneficiaries asset={asset} />
        </div>
      ))}
    </div>
  );
}

function Executors({ will }) {
  const { data: executors, loading } = useIndex('executors', { will_id: will.id });

  return (
    <div>
      <h4>Executors</h4>
      {loading ? (
        <div>Loading executors...</div>
      ) : (
        <ul>
          {executors.map((executor) => (
            <Executor key={executor.id} executor={executor} />
          ))}
        </ul>
      )}
    </div>
  );
}

function Beneficiaries({ asset }) {
  const { data: bequests, loading } = useIndex('bequests', { asset_id: asset.id });

  return (
    <div>
      <h4>Beneficiaries</h4>
      {loading ? (
        <div>Loading beneficiaries...</div>
      ) : (
        <ul>
          {bequests.map((bequest) => (
            <Beneficiary key={bequest.id} bequest={bequest} />
          ))}
        </ul>
      )}
    </div>
  );
}

function Executor({ executor }) {
  const { entity: person, handleShow, loading } = useShow('people');

  useEffect(() => {
    if (executor) {
      handleShow(executor.personId);
    }
  }, [ executor, handleShow ]);

  if (loading) {
    return <div>Loading person...</div>;
  }

  if (!person) {
    return <div>No person found</div>;
  }

  const name = `${person.firstName} ${person.lastName}`;

  return (
    <div>
      <h5>{person.name}</h5>
      <p>Name: {name}</p>
      <p>Email: {person.contactEmail}</p>
      <p>Phone: {person.contactPhone}</p>
    </div>
  );
}

function Beneficiary({ bequest }) {
  const { entity: beneficiary, handleShow, loading } = useShow('people');

  useEffect(() => {
    if (bequest.beneficiaryId) {
      handleShow(bequest.beneficiaryId);
    }
  }, [ bequest.beneficiaryId, handleShow ]);

  if (loading) {
    return <div>Loading beneficiary...</div>;
  }

  if (!beneficiary) {
    return <div>No beneficiary found</div>;
  }

  const name = `${beneficiary.firstName} ${beneficiary.lastName}`;

  return (
    <div>
      <h5>{beneficiary.name}</h5>
      <p>Name: {name}</p>
      <p>Email: {beneficiary.contactEmail}</p>
      <p>Phone: {beneficiary.contactPhone}</p>
    </div>
  );
}

export function WillComponent() {
  const navigate = useNavigate();
  const { person } = useContext(IdentityContext);
  console.log('will component', person);
  const { data, loading } = useIndex('wills', { person_id: person?.id });

  const will = data && data[0];

  useEffect(() => {
    if (will && will.status === 'draft') {
      navigate('/people/self/life-documents/will/new/continue', { replace: true });
    }
  }, [ navigate, will ]);

  if (loading) {
    return <div>Loading will...</div>;
  }

  if (!will) {
    return (
      <div className="page will-page">
        <LifeDocumentIntroHeader 
          title="Welcome to the will making process"
          description="This guided process will help you create a will in just a few simple steps."
          icon="description"
        />
        <div className="life-document-intro-sections">
          {
            sections.map((section, index) => (
              <LifeDocumentIntroSection
                key={index}
                icon={section.icon}
                title={section.title}
                content={section.content}
                subText={section.subText}
              />
            ))
          }
        </div>
        <div className="button-container" style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Link to="new">
            <Button>Create Will</Button>
          </Link>
        </div>
      </div>
    );
  }

  return <WillDataProvider will={will} />;
}

export function LifeDocumentIntroHeader({ icon,title, description }) {
  return (
    <div className="life-document-intro-header">
      <div className="top-container">
        <span className="intro-header-icon-container">
          <span className="material-symbols-outlined intro-header-icon">{icon}</span>
        </span>
        <span className="intro-text">{title}</span>
      </div>
      <p className="intro-description">{description}</p>
    </div>
  );
}

const sections = [
  {
    icon: 'alarm',
    title: 'Time Required',
    content: '15 minutes',
    subText: 'You can save your progress and return later.'
  },
  {
    icon: 'format_list_numbered',
    title: 'Steps',
    content: '6',
    subText: 'Simple guided steps'
  }
];

export function LifeDocumentIntroSection({ icon, title, content, subText }) {
  return (
    <div className="life-document-intro-section">
      <div className="top-container">
        <span className="section-header-icon-container">
          <span className="material-symbols-outlined section-header-icon">{icon}</span>
        </span>
        <span className="screen-title">{title}</span>
      </div>
      <div className="section-content">
        <p className="section-text">{content}</p>
        {subText && <p className="section-subtext">{subText}</p>}
      </div>
    </div>
  );
}